import logger from '@/lib/logger'
import { addAccessToken } from '@/utils/access-token'
import { parseBackendErrorMessage } from '@/utils/errors'

type HttpRequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD'
type MakeRequestOptions = Omit<RequestInit, 'method'> & { method?: HttpRequestMethod }

const makeRequest = async <T = void>(url: RequestInfo | URL, options: MakeRequestOptions): Promise<T> => {
    const {
        method = 'GET',
        headers = {},
        signal = AbortSignal.timeout(Number.parseInt(process.env.NEXT_PUBLIC_LATENCY_ABORT ?? 30000)),
        body,
        ...otherProps
    } = options

    await addAccessToken(headers)

    const request = new Request(url.toString(), {
        headers,
        method,
        signal,
        body,
        ...otherProps,
    })

    const response = await fetch(request)
    if (!response.ok) {
        logger.error(`${response.status}: ${response.statusText} | URL: ${url.toString()} | Method: ${method}`)
        const errorMessages: string[] = ['API Error']

        if (response.status) {
            errorMessages.push(response.status.toString())
        }

        if (response.statusText) {
            errorMessages.push(response.statusText)
        }

        const text = await response.clone().text()
        const backendError = parseBackendErrorMessage(text)

        if (backendError) {
            errorMessages.push(backendError)
        }

        const errorMessage = errorMessages.join(' | ')
        logger.serverError(errorMessage)
        throw new Error(errorMessage)
    }

    const contentType = response.headers.get('content-type')

    if (contentType?.includes('application/json')) {
        const json = (await response.json()) as T
        return json
    }

    if (contentType?.includes('text/html')) {
        const text = (await response.text()) as unknown as T
        return text
    }

    return null as T
}

export { makeRequest, type MakeRequestOptions }
